
import { useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

const SESSION_TIMEOUT_MINUTES = 15;
const WARNING_MINUTES = 2;

export const useSecureSession = () => {
  const { user, signOut } = useAuth();
  const { toast } = useToast();

  const handleSessionTimeout = useCallback(async () => {
    await signOut();
    toast({
      title: "Session Expired",
      description: "Your session has expired for security reasons. Please sign in again.",
      variant: "destructive",
    });
  }, [signOut, toast]);

  const handleSessionWarning = useCallback(() => {
    toast({
      title: "Session Expiring Soon",
      description: `Your session will expire in ${WARNING_MINUTES} minutes due to inactivity.`,
    });
  }, [toast]);

  const resetSessionTimer = useCallback(() => {
    if (!user) return;

    // Clear existing timers
    const existingWarning = localStorage.getItem('sessionWarningTimer');
    const existingTimeout = localStorage.getItem('sessionTimeoutTimer');
    
    if (existingWarning) {
      clearTimeout(parseInt(existingWarning));
      localStorage.removeItem('sessionWarningTimer');
    }
    
    if (existingTimeout) {
      clearTimeout(parseInt(existingTimeout));
      localStorage.removeItem('sessionTimeoutTimer');
    }

    // Set warning timer
    const warningTimer = setTimeout(handleSessionWarning, (SESSION_TIMEOUT_MINUTES - WARNING_MINUTES) * 60 * 1000);
    localStorage.setItem('sessionWarningTimer', warningTimer.toString());

    // Set timeout timer
    const timeoutTimer = setTimeout(handleSessionTimeout, SESSION_TIMEOUT_MINUTES * 60 * 1000);
    localStorage.setItem('sessionTimeoutTimer', timeoutTimer.toString());
  }, [user, handleSessionWarning, handleSessionTimeout]);

  useEffect(() => {
    if (user) {
      resetSessionTimer();

      // Reset timer on user activity
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
      const resetTimer = () => resetSessionTimer();

      events.forEach(event => {
        document.addEventListener(event, resetTimer, { passive: true });
      });

      return () => {
        events.forEach(event => {
          document.removeEventListener(event, resetTimer);
        });
        
        // Clear timers on cleanup
        const warningTimer = localStorage.getItem('sessionWarningTimer');
        const timeoutTimer = localStorage.getItem('sessionTimeoutTimer');
        
        if (warningTimer) {
          clearTimeout(parseInt(warningTimer));
          localStorage.removeItem('sessionWarningTimer');
        }
        
        if (timeoutTimer) {
          clearTimeout(parseInt(timeoutTimer));
          localStorage.removeItem('sessionTimeoutTimer');
        }
      };
    }
  }, [user, resetSessionTimer]);

  return { resetSessionTimer };
};
